/**
 * Knowledge (ARCHITECTURE.md §5–6, Phase 6 groundwork): search the indexed documents as
 * yourself — the ACL mirror decides what you can see — with every hit naming its document,
 * source and score; the sources and documents behind it; and the episodes, the owner deciding
 * the ones an agent proposed. Retrieval is for documents and memory, never for numbers: a
 * question about money belongs on the finance screens.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  type Entity,
  type EpisodeJson,
  type KnowledgeDocumentJson,
  type KnowledgeSearchResponse,
  type KnowledgeSourceJson,
  type User,
} from '../../api.js';

const EPISODE_KINDS = [
  'outage',
  'legal_dispute',
  'significant_complaint',
  'major_repair',
  'acquisition_decision',
  'pricing_change',
  'policy_change',
  'other',
] as const;

export function KnowledgePage(): ReactNode {
  // The same query App.tsx keeps: who is signed in decides which controls are drawn; the API
  // decides what they may do.
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ user: User | null }>('/api/me'),
    retry: false,
  });
  const isOwner = me.data?.user?.roles.includes('owner') ?? false;
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [asked, setAsked] = useState('');
  const [error, setError] = useState<string | null>(null);

  const search = useQuery({
    queryKey: ['knowledge', 'search', asked],
    enabled: asked !== '',
    queryFn: () =>
      api.get<KnowledgeSearchResponse>(`/api/knowledge/search?q=${encodeURIComponent(asked)}`),
  });
  const sources = useQuery({
    queryKey: ['knowledge', 'sources'],
    queryFn: () => api.get<{ sources: KnowledgeSourceJson[] }>('/api/knowledge/sources'),
  });
  const documents = useQuery({
    queryKey: ['knowledge', 'documents'],
    queryFn: () => api.get<{ documents: KnowledgeDocumentJson[] }>('/api/knowledge/documents'),
  });
  const episodes = useQuery({
    queryKey: ['knowledge', 'episodes'],
    queryFn: () => api.get<{ episodes: EpisodeJson[] }>('/api/knowledge/episodes'),
  });
  const entities = useQuery({
    queryKey: ['entities', 'plain'],
    queryFn: () => api.get<{ entities: Entity[] }>('/api/entities'),
  });

  const decide = useMutation({
    mutationFn: (input: { id: string; decision: 'confirmed' | 'rejected' }) =>
      api.post<{ episode: EpisodeJson }>(`/api/knowledge/episodes/${input.id}/decide`, {
        decision: input.decision,
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['knowledge', 'episodes'] });
    },
    onError: (err: Error) => setError(err.message),
  });
  const create = useMutation({
    mutationFn: (body: unknown) =>
      api.post<{ episode: EpisodeJson }>('/api/knowledge/episodes', body),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['knowledge', 'episodes'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const onSearch = (event: FormEvent): void => {
    event.preventDefault();
    setAsked(query.trim());
  };

  const onCreate = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const entityId = String(form.get('entityId') ?? '');
    create.mutate({
      kind: String(form.get('kind')),
      title: String(form.get('title')),
      summary: String(form.get('summary')),
      occurredOn: String(form.get('occurredOn')),
      entityId: entityId === '' ? null : entityId,
      links: [],
    });
    event.currentTarget.reset();
  };

  const entityName = (id: string | null): string =>
    id === null ? '—' : (entities.data?.entities.find((e) => e.id === id)?.name ?? id.slice(0, 8));

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Knowledge</h1>
          <p className="subtitle">
            The company&apos;s documents and memory. Search as yourself — you see what the source
            lets you see — and every hit names where it came from. Numbers live on the finance
            screens, not here. Policies are on <Link to="/knowledge/policies">their own page</Link>.
          </p>
        </div>
      </header>

      <section className="card">
        <h2>Search</h2>
        <form onSubmit={onSearch} className="filters">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="hot tub chemicals, lease renewal, storm damage…"
            style={{ minWidth: '24rem' }}
          />
          <button type="submit" disabled={query.trim() === ''}>
            Search
          </button>
        </form>
        {search.isLoading && <p className="muted">Searching…</p>}
        {search.isError && <p className="error">{search.error.message}</p>}
        {search.data && (
          <>
            {search.data.hits.length === 0 ? (
              <p className="muted">Nothing you can see mentions that.</p>
            ) : (
              <ol>
                {search.data.hits.map((h) => (
                  <li key={h.chunk_id}>
                    <strong>{h.title}</strong>{' '}
                    <span className="muted small">
                      {h.source_kind} · {h.source_key} · passage {h.ordinal + 1} · score{' '}
                      {h.score.toFixed(3)} (text {h.fts_rank.toFixed(3)}
                      {h.cosine !== null ? `, meaning ${h.cosine.toFixed(3)}` : ''})
                    </span>
                    <p>{h.text.length > 400 ? `${h.text.slice(0, 400)}…` : h.text}</p>
                    {h.url && (
                      <a href={h.url} target="_blank" rel="noreferrer" className="small">
                        open in the source
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            )}
            <p className="muted small">
              As {search.data.viewer} ·{' '}
              {search.data.embedding_model
                ? `question embedded with ${search.data.embedding_model}`
                : 'full text only: no embedding provider'}{' '}
              · {search.data.calc_method}
            </p>
          </>
        )}
      </section>

      <section className="card">
        <h2>Documents</h2>
        {documents.data && documents.data.documents.length === 0 && (
          <p className="muted">
            Nothing indexed yet. In development, <code>pnpm knowledge:seed-mock</code> loads a few
            sample SOPs; Drive and Gmail sources arrive with their credentials.
          </p>
        )}
        {documents.data && documents.data.documents.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Source</th>
                  <th>Modified</th>
                  <th className="num">Passages</th>
                  <th>Who can see it</th>
                </tr>
              </thead>
              <tbody>
                {documents.data.documents.map((d) => (
                  <tr key={d.id}>
                    <th scope="row">
                      {d.url ? (
                        <a href={d.url} target="_blank" rel="noreferrer">
                          {d.title}
                        </a>
                      ) : (
                        d.title
                      )}
                    </th>
                    <td>
                      {d.source_kind} · {d.source_key}
                    </td>
                    <td>{d.modified_at ? d.modified_at.slice(0, 10) : '—'}</td>
                    <td className="num">{d.chunk_count}</td>
                    <td className="small">
                      {d.acl.anyone
                        ? 'anyone signed in'
                        : [...d.acl.emails, ...d.acl.domains.map((x) => `@${x}`)].join(', ') ||
                          'everyone signed in (no restriction recorded)'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {sources.data && sources.data.sources.length > 0 && (
          <p className="muted small">
            Sources:{' '}
            {sources.data.sources
              .map((s) => `${s.name} (${s.kind}${s.is_enabled ? '' : ', disabled'})`)
              .join(' · ')}
          </p>
        )}
      </section>

      <section className="card">
        <h2>Episodes</h2>
        <p className="muted small">
          What mattered, as memory: an outage, a dispute, a major repair, a pricing change. An agent
          may propose one; only a person confirms it, and only a confirmed episode is read.
        </p>
        {error && <p className="error">{error}</p>}
        {episodes.data && episodes.data.episodes.length === 0 && (
          <p className="muted">No episodes yet.</p>
        )}
        {episodes.data && episodes.data.episodes.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Kind</th>
                <th>Episode</th>
                <th>Entity</th>
                <th>Status</th>
                {isOwner && <th />}
              </tr>
            </thead>
            <tbody>
              {episodes.data.episodes.map((e) => (
                <tr key={e.id} className={e.status === 'rejected' ? 'muted' : ''}>
                  <td>{e.occurred_on}</td>
                  <td>{e.kind.replace(/_/g, ' ')}</td>
                  <td>
                    <strong>{e.title}</strong>
                    <p className="small">{e.summary}</p>
                    {e.derivation === 'ai_inferred' && (
                      <span className="muted small">
                        proposed by an agent run {e.proposed_by_run_id?.slice(0, 8)}
                      </span>
                    )}
                  </td>
                  <td>{entityName(e.entity_id)}</td>
                  <td>
                    <span
                      className={
                        e.status === 'confirmed'
                          ? 'status status-approved'
                          : e.status === 'proposed'
                            ? 'status status-pending'
                            : 'status'
                      }
                    >
                      {e.status}
                    </span>
                  </td>
                  {isOwner && (
                    <td>
                      {e.status === 'proposed' && (
                        <>
                          <button
                            type="button"
                            onClick={() => decide.mutate({ id: e.id, decision: 'confirmed' })}
                          >
                            Confirm
                          </button>{' '}
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => decide.mutate({ id: e.id, decision: 'rejected' })}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {isOwner && (
          <form onSubmit={onCreate} className="form-grid">
            <h3>Record an episode</h3>
            <label>
              Kind{' '}
              <select name="kind" defaultValue="other">
                {EPISODE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>
            <label>
              When <input type="date" name="occurredOn" required />
            </label>
            <label>
              Entity{' '}
              <select name="entityId" defaultValue="">
                <option value="">none</option>
                {(entities.data?.entities ?? []).map((en) => (
                  <option key={en.id} value={en.id}>
                    {en.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Title <input name="title" required maxLength={200} />
            </label>
            <label>
              What happened <textarea name="summary" required maxLength={4000} rows={3} />
            </label>
            <button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Saving…' : 'Record'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
